"use client";

import React from "react";
import { Phone, Mail, MapPin, Linkedin, Send, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
function FooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 animate-pulse rounded bg-secondary-foreground/10" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-secondary-foreground/70 transition-all hover:text-primary hover:underline hover:decoration-primary hover:underline-offset-4">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="rounded-md border border-primary/20 bg-primary/10 p-3">
        <p className="text-sm font-medium text-primary">Thank you for subscribing!</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="flex gap-2">
                <input {...field} type="email" placeholder="Email address" className="flex h-9 w-full rounded-md border border-secondary-foreground/20 bg-secondary-foreground/5 px-3 py-1 text-sm text-secondary-foreground shadow-sm transition-colors placeholder:text-secondary-foreground/40 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50" />
                <Button type="submit" disabled={isSubmitting} size="sm" className="shrink-0">
                  {isSubmitting ? <Loader className="size-4 animate-spin" /> : <Send className="size-4" />}
                  <span className="sr-only">Subscribe</span>
                </Button>
              </div>
            </FormControl>
            <FormMessage className="text-xs text-destructive" />
          </FormItem>} />
    </Form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="bg-secondary text-secondary-foreground">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 lg:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587918/img/scott_smith_consulting_logo_footer.png" alt="Scott Smith Consulting" className="h-7 w-auto" />
            </Link>
            <p className="text-sm leading-relaxed text-secondary-foreground/70">
              Scott Smith Consulting is dedicated to helping organizations identify hazards, develop compliance programs, and implement risk mitigation strategies with practical, tailored safety solutions.
            </p>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-secondary-foreground">
              Quick Links
            </h3>
            <MenuProvider menu_id="17">
              <FooterMenu />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-secondary-foreground">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li data-index={0} className="flex items-start gap-3 text-sm text-secondary-foreground/70">
                <Phone className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>Scott Smith, Safety Consultant | Scott Smith Consulting, Cell: (714) 345-6144</span>
              </li>
              <li data-index={1} className="flex items-start gap-3 text-sm text-secondary-foreground/70">
                <Mail className="mt-0.5 size-4 shrink-0 text-primary" />
                <Link to="mailto:safetysmithca@yahoo.com" className="transition-colors hover:text-primary hover:underline hover:decoration-primary hover:underline-offset-4">
                  safetysmithca@yahoo.com
                </Link>
              </li>
              <li data-index={2} className="flex items-start gap-3 text-sm text-secondary-foreground/70">
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>Eastvale CA</span>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-secondary-foreground">
              Stay Updated
            </h3>
            <p className="text-sm text-secondary-foreground/70">
              Subscribe to our newsletter for the latest safety insights and regulatory updates.
            </p>
            <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
            email: ""
          }}>
              <NewsletterForm />
            </FormProvider>
            <div className="pt-2">
              <Link to="#" className="inline-flex size-9 items-center justify-center rounded-md bg-secondary-foreground/5 text-secondary-foreground/70 transition-colors hover:bg-primary hover:text-primary-foreground" aria-label="LinkedIn">
                <Linkedin className="size-4" />
              </Link>
            </div>
          </div>
        </div>

        {}
        <Separator className="my-8 bg-secondary-foreground/10" />
        
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-xs text-secondary-foreground/50">
            &copy; {currentYear} Scott Smith Consulting. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="#" className="text-xs text-secondary-foreground/50 transition-colors hover:text-primary hover:underline hover:decoration-primary hover:underline-offset-4">
              Privacy Policy
            </Link>
            <Link to="#" className="text-xs text-secondary-foreground/50 transition-colors hover:text-primary hover:underline hover:decoration-primary hover:underline-offset-4">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </section>;
}